import React from 'react';
import {useRouter} from 'expo-router';
import {Share, Platform} from 'react-native';
import Alert from "@/lib/Alert";
import {AppDataMediaItem} from '@/data/app-data-interfaces';
import {useOrganizationData} from "@/data/app-data-hooks";
import notFound from "@/app/+not-found";
import {MediaDetail} from "@/components/media/video-detail/MediaDetail";
import {VideoSource} from "@/components/media/VideoSource";
import {useBookmarks, useLastViewed} from "@/data/user-data-hooks";

interface VideoDetailPageProps {
    orgId: string;
    videoId: string;
    source: VideoSource;
    folderId?: string; // Only relevant when source is 'folder'
}

export function VideoDetailPage({orgId, videoId, source, folderId}: VideoDetailPageProps) {
    const router = useRouter();

    // Get organization data
    const {data: orgData} = useOrganizationData(orgId);
    const bookmarks = useBookmarks(orgId);
    const lastViewed = useLastViewed(orgId);

    // Find the current video
    const currentVideo = orgData.media.items[videoId];

    if (!currentVideo) {
        return notFound();
    }

    // Get the video list based on source
    const getVideoList = (): AppDataMediaItem[] => {
        const allVideos = Object.values(orgData.media.items);

        switch (source) {
            case 'featured':
                return orgData.media.featured
                    .map(id => orgData.media.items[id])
                    .filter(Boolean)
                    .sort((a, b) => a.sorting - b.sorting);

            case 'favorites':
                // Assuming favorites would be stored somewhere in user data
                // For now, return empty array as the structure isn't defined
                return bookmarks.map(id => orgData.media.items[id])
                    .filter(Boolean)
                    .reverse();

            case 'folder':
                const currentFolderId = folderId || currentVideo.folderId;
                return allVideos
                    .filter(video => video.folderId === currentFolderId)
                    .sort((a, b) => a.sorting - b.sorting);

            case 'last-viewed':
                return lastViewed.map(last => orgData.media.items[last.videoId]).filter(Boolean);

            default:
                return [];
        }
    };

    const videoList = getVideoList();
    const currentIndex = videoList.findIndex(video => video.id === videoId);

    // Get previous and next videos
    const previousVideo = currentIndex > 0 ? videoList[currentIndex - 1] : null;
    const nextVideo = currentIndex < videoList.length - 1 ? videoList[currentIndex + 1] : null;

    // Get related videos (excluding current video)
    const getRelatedVideos = (): AppDataMediaItem[] => {
        const allVideos = Object.values(orgData.media.items);

        // Get videos from the same folder, excluding current video
        const sameFolder = allVideos
            .filter(video =>
                video.folderId === currentVideo.folderId &&
                video.id !== currentVideo.id
            )
            .sort((a, b) => a.sorting - b.sorting)
            .slice(0, 3); // Limit to 3 related videos

        // If not enough from same folder, add other videos
        if (sameFolder.length < 3) {
            const otherVideos = allVideos
                .filter(video =>
                    video.folderId !== currentVideo.folderId &&
                    video.id !== currentVideo.id &&
                    !sameFolder.find(v => v.id === video.id)
                )
                .sort((a, b) => a.sorting - b.sorting)
                .slice(0, 3 - sameFolder.length);

            return [...sameFolder, ...otherVideos];
        }

        return sameFolder;
    };

    const relatedVideos = getRelatedVideos();

    // Navigation handlers
    const handleBack = () => {
        if (Platform.OS === 'web') {
            // On web, use browser back button since navigation is via query strings
            window.history.back();
        } else {
            // On mobile, use router back
            router.back();
        }
    };

    const handleShare = async () => {
        try {
            const shareUrl = `https://app.octahub.eu/organization/${orgId}/video/${videoId}?source=${source}${folderId ? `&folderId=${folderId}` : ''}`;
            await Share.share({
                message: `Check out this video: ${currentVideo.name} ${shareUrl}`,
                url: shareUrl,
                title: currentVideo.name,
            });
        } catch {
            Alert.alert('Error', 'Failed to share video');
        }
    };

    const handlePreviousVideo = () => {
        if (previousVideo) {
            router.replace({
                pathname: `/organization/${orgId}/video/${previousVideo.id}` as any,
                params: {source, folderId}
            });
        }
    };

    const handleNextVideo = () => {
        if (nextVideo) {
            router.replace({
                pathname: `/organization/${orgId}/video/${nextVideo.id}` as any,
                params: {source, folderId}
            });
        }
    };

    const onRelatedSelect = (videoId: string) => {
        router.replace({
            pathname: `/organization/${orgId}/video/${videoId}` as any,
            params: {source, folderId}
        });
    }

    const handleDelete = () => {
        handleBack();
    };

    return (
        <MediaDetail
            orgId={orgId}
            mediaItem={currentVideo}
            onBack={handleBack}
            // onShare={handleShare}
            onPreviousVideo={handlePreviousVideo}
            onNextVideo={handleNextVideo}
            onRelatedSelect={onRelatedSelect}
            previousVideo={previousVideo}
            nextVideo={nextVideo}
            relatedVideos={relatedVideos}
            onDeleted={handleDelete}
        />
    );
}
